import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: number;
  role: 'bot' | 'user';
  text: string;
  timestamp: Date;
}

const KNOWLEDGE_BASE = {
  company: {
    name: 'CzechAI',
    founded: '2022',
    description: 'CzechAI je váš partner pro AI transformaci. Od roku 2022 pomáháme firmám digitalizovat a automatizovat. V roce 2025 jsme AI vyčlenili do samostatné firmy CzechAI, abychom doručovali rychleji a s měřitelnými výsledky.',
    mission: 'Zpřístupnit pokročilé AI technologie firmám všech velikostí a proměnit je v konkrétní dopady: úspora času, nižší náklady, nový růst.',
    stats: '150+ spokojených klientů, 95% míra spokojenosti, 500+ implementovaných řešení, 24/7 technická podpora',
    aurumAI: 'Aurum AI je náš proprietární AI stack s českým jazykovým modelem, agentním orchestrem a bezpečným RAG nad vašimi daty. Nasazení on-prem / private cloud – data zůstávají u vás.',
  },
  contacts: {
    general: '+420 732 941 900 | info@czechai.io',
    prague: '+420 704 105 520 | praha@czechai.io',
    brno: '+420 775 993 829 | brno@czechai.io',
    hours: 'Po–Pá: 8:00–17:00, So–Ne: Na domluvě',
  },
  certifications: [
    'AWS Certified Solutions Architect',
    'Microsoft Azure AI Engineer',
    'Google Cloud Professional ML Engineer',
    'ISO 27001 Information Security',
    'GDPR Compliance Specialist',
  ],
  partners: ['AurumAI', 'Verum Capital', 'All In Web', 'Bohemia Shield'],
  integrations: [
    'Salesforce', 'HubSpot', 'SAP', 'Odoo', 'POHODA', 'Google Workspace',
    'Microsoft 365', 'PostgreSQL', 'MySQL', 'MS SQL Server', 'Power BI',
    'Looker Studio', 'Slack', 'Microsoft Teams', 'WhatsApp Business API',
    'AWS', 'Microsoft Azure', 'Google Cloud Platform', 'UiPath (RPA)',
    'Make (Integromat)', 'Zapier', 'Shoptet',
  ],
  services: [
    {
      id: 'obchod-zakaznici',
      title: 'AI pro obchod a zákazníky',
      items: ['AI Obchodní zástupce', 'AI Customer Support', 'AI Recepční / Asistentka', 'AI Personalista (HR)', 'AI Social Media Manager', 'AI Copywriter & SEO', 'AI Právní asistent', 'AI Finanční analytik', 'AI Sales Coach', 'AI Voice-to-CRM'],
    },
    {
      id: 'automatizace-procesu',
      title: 'Automatizace procesů',
      items: ['Automatické zpracování faktur', 'Automatizace skladu & logistiky', 'Automatické reporty (KPI)', 'AI správa dokumentů', 'Automatizace onboardingu', 'AI Meeting Assistant', 'AI Contract Management', 'AI Expense Control'],
    },
    {
      id: 'chatboti-servis',
      title: 'Chatboti & zákaznický servis',
      items: ['Hyper-personalizovaný nákupní asistent', 'AI Hlasový asistent', 'Lead-gen chatbot', 'Analytický chatbot', 'Proaktivní servisní chatbot', 'Multijazyčný AI chatbot'],
    },
    {
      id: 'marketing-prodej',
      title: 'Marketing & prodej',
      items: ['Prediktivní analýza zákazníků', 'Dynamická cenotvorba', 'Personalizace webu & e-mailů', 'AI pro influencer marketing', 'Generování vizuálů & videí', 'AI Ad Optimizer', 'Generátor landing pages'],
    },
    {
      id: 'skoleni-rozvoj',
      title: 'Školení & rozvoj',
      items: ['Personalizované vzdělávání', 'AI Simulátor rozhovorů', 'AI Jazykový kouč', 'AI školení compliance/BOZP', 'AI Leadership Mentor', 'Microlearning platforma'],
    },
    {
      id: 'analytika-bi',
      title: 'Analytika & BI',
      items: ['Analýza sentimentu', 'Predikce fluktuace zaměstnanců', 'Konkurenční analýza', 'Optimalizace výroby', 'AI Cashflow Predictor', 'Sustainability Analytics'],
    },
    {
      id: 'odborne-sluzby',
      title: 'Odborné a důvěrné služby',
      items: ['AI Due Diligence', 'Generativní AI strategie', 'AI Risk Scoring', 'Scenario Planning AI'],
    },
    {
      id: 'research-analyzy',
      title: 'Research & analýzy',
      items: ['Due diligence firem', 'Customer persona research', 'Tender monitoring', 'Investment screening', 'Compliance monitoring', 'Brand sentiment analysis', 'Konkurenční monitoring', 'Lead generation', 'Market research', 'Supplier scouting', 'Talent Market Analysis', 'Technology Radar'],
    },
    {
      id: 'content-marketing',
      title: 'Content & marketing',
      items: ['SEO články a blogy', 'Plánování sociálních sítí', 'E-mail marketing kampaně', 'Whitepapers a case studies', 'Prezentace a pitch decky', 'Produktové popisy', 'Tiskové zprávy', 'LinkedIn branding', 'Videoscénáře', 'Newslettery', 'AI Design Assistant', 'Podcast & Audio generátor'],
    },
    {
      id: 'automatizace-monitoring',
      title: 'Automatizace & monitoring',
      items: ['Automatizace opakujících se úkolů', 'Chatboty pro zákaznickou podporu', 'Personalizace marketingových kampaní', 'Školení zaměstnanců pomocí AI modulů', 'Monitoring procesů a KPI', 'Prediktivní monitoring', 'Real estate portfolio analýza', 'Optimalizace zásob', 'Sales forecasting', 'Facility Management AI', 'AI Security Monitoring'],
    },
  ],
  faq: [
    { q: 'implementace', a: 'Jednoduché chatboty spustíme během 2–3 týdnů, komplexní systémy trvají 2–6 měsíců. Vždy začínáme pilotním projektem.' },
    { q: 'cena', a: 'Základní automatizace začíná na desítkách tisíc korun, komplexní systémy ve stovkách tisíc. Nabízíme různé cenové modely včetně měsíčních plateb.' },
    { q: 'it tým', a: 'Ne, naše řešení jsou navržena pro běžné uživatele. Zajišťujeme kompletní zaškolení a dlouhodobou technickou podporu.' },
    { q: 'bezpečnost', a: 'Používáme nejmodernější šifrování, pravidelné bezpečnostní audity a splňujeme požadavky GDPR.' },
    { q: 'pilot', a: 'Ano, doporučujeme zahájit spolupráci testovacím projektem, na kterém si ověříte výhody AI pro vaše konkrétní potřeby.' },
  ],
};

function getBotResponse(input: string): string {
  const text = input.toLowerCase();

  // Greetings
  if (/^(ahoj|dobrý den|zdravím|čau|hej|hi|hello)/.test(text)) {
    return 'Dobrý den! 👋 Jsem AI asistent CzechAI. Mohu vám pomoci s informacemi o našich službách, cenách, implementaci nebo vás spojit s naším týmem. Na co se chcete zeptat?';
  }

  // Contact / reach out
  if (/kontakt|zavolat|napsat|email|telefon|spojit|konzultac|formulář|poptávk/.test(text)) {
    return `Rádi vás spojíme s naším týmem! 📞\n\n**Vedení:** +420 732 941 900 | info@czechai.io\n**Praha:** +420 704 105 520 | praha@czechai.io\n**Brno:** +420 775 993 829 | brno@czechai.io\n\nNebo rovnou vyplňte náš kontaktní formulář — ozveme se do 24 hodin. 👉 [Přejít na formulář](/kontakt)`;
  }

  // Price / cost
  if (/cen|kolik stoj|cena|náklad|platb|investic/.test(text)) {
    return `Cena závisí na rozsahu projektu:\n\n💰 **Základní automatizace** – od desítek tisíc Kč\n💰 **Komplexní systémy** – stovky tisíc Kč\n\nNabízíme různé cenové modely včetně měsíčních plateb. Pro přesnou nabídku nás kontaktujte:\n📞 +420 732 941 900\n📧 info@czechai.io\n\nNebo vyplňte [kontaktní formulář](/kontakt) a připravíme vám nabídku na míru.`;
  }

  // Implementation time
  if (/jak dlouho|doba|trvá|implementac|nasazen|spuštěn/.test(text)) {
    return `Doba implementace závisí na složitosti:\n\n⚡ **Chatbot / jednoduchá automatizace** – 2–3 týdny\n🔧 **Komplexní systémy** – 2–6 měsíců\n\nVždy začínáme rychlým pilotním projektem (2–4 týdny), kde si vše otestujete v praxi. Chcete se dozvědět více? 📞 +420 732 941 900`;
  }

  // Services overview
  if (/služb|co nabíz|co děláte|co umíte|portfolio|kategori/.test(text)) {
    return `CzechAI nabízí **10 kategorií AI řešení** s 80+ nástroji:\n\n1. AI pro obchod a zákazníky\n2. Automatizace procesů\n3. Chatboti & zákaznický servis\n4. Marketing & prodej\n5. Školení & rozvoj\n6. Analytika & BI\n7. Odborné a důvěrné služby\n8. Research & analýzy\n9. Content & marketing\n10. Automatizace & monitoring\n\nChcete vědět více o konkrétní kategorii? Nebo rovnou [zobrazit všechny služby](/sluzby)?`;
  }

  // Chatbot service
  if (/chatbot|chat bot|virtuální asistent|hlasový asistent|zákaznický servis/.test(text)) {
    return `Naše chatbot řešení zahrnují:\n\n🤖 Hyper-personalizovaný nákupní asistent\n🎙️ AI Hlasový asistent\n📊 Lead-gen chatbot\n🌍 Multijazyčný AI chatbot\n🔔 Proaktivní servisní chatbot\n\nChatboty spustíme během **2–3 týdnů** a fungují 24/7. Zajímá vás demo nebo konzultace?\n📞 +420 732 941 900 | [Kontaktní formulář](/kontakt)`;
  }

  // Automation
  if (/automatizac|automat|procesy|workflow|faktur|sklad|report/.test(text)) {
    return `Automatizace procesů je naše silná stránka! Nabízíme:\n\n⚙️ Automatické zpracování faktur\n📦 Automatizace skladu & logistiky\n📈 Automatické reporty (KPI)\n📄 AI správa dokumentů\n👥 Automatizace onboardingu\n📅 AI Meeting Assistant\n\nŠetříme firmám desítky hodin týdně. Chcete analýzu vašich procesů?\n📞 +420 732 941 900 | [Kontaktní formulář](/kontakt)`;
  }

  // Marketing
  if (/marketing|prodej|reklam|seo|obsah|content|social media|kampaň/.test(text)) {
    return `V oblasti marketingu a prodeje nabízíme:\n\n🎯 Prediktivní analýza zákazníků\n💲 Dynamická cenotvorba\n✉️ Personalizace webu & e-mailů\n🎨 Generování vizuálů & videí\n📝 SEO články, blogy, newslettery\n📱 Plánování sociálních sítí\n\nChcete zvýšit konverze a ušetřit čas na tvorbě obsahu?\n📞 +420 732 941 900 | [Kontaktní formulář](/kontakt)`;
  }

  // Analytics / BI
  if (/analytik|analýz|data|bi|predikc|report|dashboard/.test(text)) {
    return `Naše analytická řešení zahrnují:\n\n📊 Analýza sentimentu zákazníků\n👥 Predikce fluktuace zaměstnanců\n🔍 Konkurenční analýza\n🏭 Optimalizace výroby\n💰 AI Cashflow Predictor\n🌱 Sustainability Analytics\n\nProměňte vaše data v konkrétní rozhodnutí. Kontaktujte nás:\n📞 +420 732 941 900 | [Kontaktní formulář](/kontakt)`;
  }

  // Security / GDPR / data
  if (/bezpečnost|gdpr|data|ochrana|šifrování|soukromí|on.prem/.test(text)) {
    return `Bezpečnost dat je naší prioritou:\n\n🔒 Nasazení on-prem / private cloud – data zůstávají u vás\n🛡️ ISO 27001 Information Security certifikace\n✅ GDPR Compliance Specialist\n🔐 Nejmodernější šifrování a pravidelné audity\n📋 Governance & audit – logování dotazů, verze promptů\n\nNaše vlastní platforma **Aurum AI** zajišťuje plnou kontrolu nad vašimi daty.`;
  }

  // Aurum AI
  if (/aurum|vlastní ai|proprietární|jazykový model/.test(text)) {
    return `**Aurum AI** je náš vlastní AI stack:\n\n🧠 Český jazykový model\n🤖 Agentní orchestr\n🔒 Bezpečný RAG nad vašimi daty\n☁️ Nasazení on-prem nebo private cloud\n📋 Governance & audit\n🔧 Modulární architektura\n\nData zůstávají vždy u vás – žádné sdílení s třetími stranami. Chcete vědět více?\n📞 +420 732 941 900`;
  }

  // About company
  if (/o vás|o firmě|kdo jste|czechai|historie|kdy vznikl|o nás/.test(text)) {
    return `**CzechAI** – váš partner pro AI transformaci 🚀\n\n📅 Fungujeme od roku 2022\n🏆 150+ úspěšných projektů\n✅ 95% míra spokojenosti klientů\n🌍 Pokrýváme celou ČR (Praha, Brno)\n\nNaše mise: zpřístupnit AI technologie firmám všech velikostí. Dodáváme řešení, ne sliby – rychlé piloty (2–4 týdny), jasné KPI.\n\nVíce na stránce [O nás](/o-nas).`;
  }

  // Certifications
  if (/certifikac|certifikát|aws|azure|google cloud|iso/.test(text)) {
    return `Náš tým drží tyto certifikace:\n\n🏅 AWS Certified Solutions Architect\n🏅 Microsoft Azure AI Engineer\n🏅 Google Cloud Professional ML Engineer\n🏅 ISO 27001 Information Security\n🏅 GDPR Compliance Specialist\n\nPracujeme s předními technologickými partnery: AurumAI, Verum Capital, All In Web, Bohemia Shield.`;
  }

  // Integrations
  if (/integrac|napojení|systém|erp|crm|sap|salesforce|hubspot|pohoda/.test(text)) {
    return `Napojujeme se na 22+ systémů:\n\n💼 CRM: Salesforce, HubSpot\n🏭 ERP: SAP, Odoo, POHODA\n☁️ Cloud: AWS, Azure, Google Cloud\n📊 BI: Power BI, Looker Studio\n💬 Komunikace: Slack, Teams, WhatsApp\n🔄 Automatizace: Make, Zapier, UiPath\n🛒 E-commerce: Shoptet\n\nMáte specifický systém? Kontaktujte nás:\n📞 +420 732 941 900`;
  }

  // IT team / requirements
  if (/it tým|vlastní tým|znalosti|technické|zaměstnanci|školení/.test(text)) {
    return `Nepotřebujete vlastní IT tým! 👍\n\nNaše řešení jsou navržena pro běžné uživatele:\n\n✅ Kompletní zaškolení vašeho týmu\n✅ Dlouhodobá technická podpora 24/7\n✅ Intuitivní rozhraní bez nutnosti programování\n✅ Průvodce celým procesem implementace\n\nZačněte pilotním projektem a přesvědčte se sami. [Kontaktujte nás](/kontakt)`;
  }

  // Pilot project
  if (/pilot|test|zkušební|vyzkoušet|demo/.test(text)) {
    return `Skvělý nápad! 🎯 Doporučujeme začít pilotním projektem:\n\n⚡ Spuštění za **2–4 týdny**\n📊 Jasné KPI a měřitelné výsledky\n💡 Minimální riziko – otestujete vše v praxi\n🔧 Minimální dopad na vaše běžné procesy\n\nPilotní projekt je nejlepší způsob, jak si ověřit výhody AI pro vaši firmu. Domluvme se:\n📞 +420 732 941 900\n📧 info@czechai.io\n👉 [Kontaktní formulář](/kontakt)`;
  }

  // Industries
  if (/průmysl|výroba|e-commerce|retail|logistika|zdravotnictví|finance|hr|školství|energetika/.test(text)) {
    return `CzechAI implementuje AI řešení napříč odvětvími:\n\n🛒 E-commerce & retail\n🏭 Výroba & průmysl\n📞 Zákaznický servis & callcentra\n🏦 Finance & reality\n👥 HR & nábor\n🚚 Logistika & doprava\n⚡ Energetika & utility\n🎓 Školství & vzdělávání\n🏛️ Stát & veřejný sektor\n\nChcete vědět, jak AI pomůže ve vašem odvětví?\n📞 +420 732 941 900 | [Kontaktní formulář](/kontakt)`;
  }

  // Platforms
  if (/platforma|chat\.czechai|vip\.czechai|leads\.czechai|leady/.test(text)) {
    return `CzechAI provozuje tři specializované platformy:\n\n💬 **PLATFORMA** – chat.czechai.io – AI chatovací platforma\n⭐ **VIP** – vip.czechai.io – VIP klientský portál\n📊 **LEADY** – leads.czechai.io – Lead generation platforma\n\nChcete se dozvědět více o některé z platforem?\n📞 +420 732 941 900 | info@czechai.io`;
  }

  // Default fallback
  return `Děkuji za váš dotaz! 😊 Nejsem si jistý, jak přesně odpovědět, ale náš tým vám rád pomůže osobně.\n\n📞 **Zavolejte nám:**\n• Vedení: +420 732 941 900\n• Praha: +420 704 105 520\n• Brno: +420 775 993 829\n\n📧 **Napište nám:** info@czechai.io\n\n👉 Nebo vyplňte [kontaktní formulář](/kontakt) a ozveme se do 24 hodin!`;
}

const QUICK_REPLIES = [
  { label: '💼 Naše služby', text: 'Jaké služby nabízíte?' },
  { label: '💰 Ceny', text: 'Kolik stojí implementace?' },
  { label: '⚡ Jak rychle?', text: 'Jak dlouho trvá implementace?' },
  { label: '📞 Kontakt', text: 'Jak vás mohu kontaktovat?' },
  { label: '🤖 Chatboti', text: 'Jaké chatbot řešení nabízíte?' },
  { label: '🔒 Bezpečnost', text: 'Jak zajišťujete bezpečnost dat?' },
];

function renderText(text: string, navigate: ReturnType<typeof useNavigate>) {
  const parts = text.split(/(\[.*?\]\(.*?\)|\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
    if (linkMatch) {
      return (
        <button
          key={i}
          onClick={() => navigate(linkMatch[2])}
          className="text-white underline underline-offset-2 font-semibold hover:opacity-80 cursor-pointer"
        >
          {linkMatch[1]}
        </button>
      );
    }
    const boldMatch = part.match(/\*\*(.*?)\*\*/);
    if (boldMatch) {
      return <strong key={i}>{boldMatch[1]}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      role: 'bot',
      text: 'Dobrý den! 👋 Jsem AI asistent CzechAI. Mohu vám pomoci s informacemi o našich AI službách, cenách nebo vás spojit s naším týmem. Na co se chcete zeptat?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
      setHasUnread(false);
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (!isOpen && messages.length > 1) {
      setHasUnread(true);
    }
  }, [messages]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;

    const userMsg: Message = {
      id: Date.now(),
      role: 'user',
      text: text.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const response = getBotResponse(text.trim());
      const botMsg: Message = {
        id: Date.now() + 1,
        role: 'bot',
        text: response,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 700 + Math.random() * 500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setIsOpen(!isOpen); setHasUnread(false); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-black text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-gray-800 transition-all duration-300 cursor-pointer"
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}
        aria-label="Otevřít chat"
      >
        {isOpen ? (
          <i className="ri-close-line text-2xl"></i>
        ) : (
          <>
            <i className="ri-chat-3-line text-2xl"></i>
            {hasUnread && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"></span>
            )}
          </>
        )}
      </button>

      {/* Chat window */}
      <div
        className={`fixed bottom-24 right-6 z-50 w-[370px] max-w-[calc(100vw-24px)] bg-white rounded-2xl shadow-2xl flex flex-col transition-all duration-300 origin-bottom-right ${
          isOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
        }`}
        style={{ height: '560px', maxHeight: 'calc(100vh - 120px)', boxShadow: '0 16px 64px rgba(0,0,0,0.18)' }}
      >
        {/* Header */}
        <div className="bg-black text-white px-5 py-4 rounded-t-2xl flex items-center gap-3 flex-shrink-0">
          <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center">
            <i className="ri-robot-2-line text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm leading-tight">CzechAI Asistent</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-2 h-2 bg-green-400 rounded-full inline-block"></span>
              <span className="text-xs text-gray-300">Online · Odpovídám ihned</span>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'bot' && (
                <div className="w-7 h-7 bg-black rounded-full flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                  <i className="ri-robot-2-line text-white text-xs"></i>
                </div>
              )}
              <div
                className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                  msg.role === 'user'
                    ? 'bg-black text-white rounded-br-sm'
                    : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm'
                }`}
              >
                {msg.role === 'bot'
                  ? renderText(msg.text, navigate)
                  : msg.text}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="w-7 h-7 bg-black rounded-full flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                <i className="ri-robot-2-line text-white text-xs"></i>
              </div>
              <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick replies */}
        {messages.length <= 2 && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {QUICK_REPLIES.map((qr) => (
                <button
                  key={qr.label}
                  onClick={() => sendMessage(qr.text)}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:bg-black hover:text-white hover:border-black transition-colors cursor-pointer whitespace-nowrap"
                >
                  {qr.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={handleSubmit}
          className="px-4 py-3 bg-white border-t border-gray-100 rounded-b-2xl flex items-center gap-2 flex-shrink-0"
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Napište zprávu..."
            className="flex-1 text-sm px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:border-gray-400 transition-colors"
          />
          <button
            type="submit"
            disabled={!input.trim() || isTyping}
            className="w-9 h-9 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-send-plane-fill text-sm"></i>
          </button>
        </form>
      </div>
    </>
  );
}
